import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Search, Eye, Edit, Trash2, Calendar, User, Car, DollarSign, Filter, X, Building2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import bookingService from '../../services/bookingService';
import { useLanguage } from '../../context/LanguageContext';
import BookingAdminDetail from '../../components/admin/bookings/BookingAdminDetail';

/**
 * Bookings List Page
 * Admin can view and manage all bookings with filters
 */
const BookingsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  // État pour stocker le nom de l'agence filtrée
  const [filteredAgencyName, setFilteredAgencyName] = useState(null);

  // Filters - Initialiser avec les paramètres d'URL
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    vendorId: searchParams.get('vendorId') || '',
    paymentStatus: '',
    startDate: '',
    endDate: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  // Deep-link depuis l'audit trail : ?bookingId=
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (bookingId) {
      setSelectedBookingId(bookingId);
      setDetailDrawerOpen(true);
    }
  }, [searchParams]);

  // Status options
  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'PENDING', label: 'En attente' },
    { value: 'CONFIRMED', label: 'Confirmée' },
    { value: 'LIVE', label: 'En cours' },
    { value: 'COMPLETED', label: 'Terminée' },
    { value: 'CANCELLED', label: 'Annulée' },
    { value: 'DECLINED', label: 'Refusée' },
    { value: 'EXPIRED', label: 'Expirée' },
    { value: 'REFUNDED', label: 'Remboursée' },
  ];

  const paymentStatusOptions = [
    { value: '', label: 'Tous les statuts de paiement' },
    { value: 'PENDING', label: 'En attente' },
    { value: 'PAID', label: 'Payé' },
    { value: 'REFUNDED', label: 'Remboursé' },
  ];

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Charger le nom de l'agence si vendorId est présent
  useEffect(() => {
    const loadAgencyName = async () => {
      if (filters.vendorId) {
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies/${filters.vendorId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data?.personalInfo) {
              setFilteredAgencyName(result.data.personalInfo.nom);
            }
          }
        } catch (error) {
          console.error('Error loading agency name:', error);
        }
      } else {
        setFilteredAgencyName(null);
      }
    };

    loadAgencyName();
  }, [filters.vendorId]);

  const loadBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: entriesPerPage,
        search: searchTerm,
        sortBy: sortConfig.key || 'createdAt',
        sortOrder: sortConfig.direction,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await bookingService.getAllBookings(params);
      
      if (response.success) {
        setBookings(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      alert('Erreur lors du chargement des réservations: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, entriesPerPage, searchTerm, sortConfig, filters]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadBookings();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      vendorId: '',
      paymentStatus: '',
      startDate: '',
      endDate: '',
    });
    setCurrentPage(1);
    setFilteredAgencyName(null);
    // Nettoyer les paramètres d'URL
    setSearchParams({});
  };

  // Vérifier si des filtres sont actifs (y compris vendorId)
  const hasActiveFilters = filters.status || filters.vendorId || filters.paymentStatus || filters.startDate || filters.endDate;

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('bookings.status.pending') },
      CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('bookings.status.confirmed') },
      LIVE: { bg: 'bg-green-100', text: 'text-green-800', label: t('bookings.status.live') },
      COMPLETED: { bg: 'bg-gray-100', text: 'text-gray-800', label: t('bookings.status.completed') },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: t('bookings.status.cancelled') },
      DECLINED: { bg: 'bg-orange-100', text: 'text-orange-800', label: t('bookings.status.declined') },
      EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-800', label: t('bookings.status.expired') },
      REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-800', label: t('bookings.status.refunded') },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('bookings.paymentStatus.pending') },
      PAID: { bg: 'bg-green-100', text: 'text-green-800', label: t('bookings.paymentStatus.paid') },
      REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-800', label: t('bookings.paymentStatus.refunded') },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentMethodBadge = (paymentMethod) => {
    // Si la valeur est '0' ou 'cash', afficher 'Espèces'
    const method = paymentMethod?.toLowerCase();
    if (method === '0' || method === 'cash') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Espèces
        </span>
      );
    }
    // Pour les autres méthodes, afficher la valeur telle quelle ou 'N/A'
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {paymentMethod || 'N/A'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount, currency = 'MAD') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleViewDetails = (bookingId) => {
    setSelectedBookingId(bookingId);
    setDetailDrawerOpen(true);
  };

  const closeDetailDrawer = () => {
    setDetailDrawerOpen(false);
    setSelectedBookingId(null);
    if (searchParams.get('bookingId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('bookingId');
      setSearchParams(next);
    }
  };

  const handleEdit = (bookingId) => {
    // TODO: Naviguer vers la page d'édition ou ouvrir un modal
    console.log('Edit booking:', bookingId);
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm(t('bookings.deleteConfirm'))) {
      return;
    }

    try {
      await bookingService.deleteBooking(bookingId);
      alert(t('bookings.deleteSuccess'));
      loadBookings();
    } catch (error) {
      alert(t('bookings.deleteError') + ': ' + error.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('bookings.title')}</h1>
          <p className="text-gray-600">{t('bookings.subtitle')}</p>
        </div>

        {/* Message de filtres actifs */}
        {(filters.vendorId || filters.status) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Résultats filtrés
                  {filteredAgencyName && (
                    <span className="ml-1">pour l'agence <strong>{filteredAgencyName}</strong></span>
                  )}
                  {filters.status && (
                    <span className="ml-1">
                      - Statut: <strong>{statusOptions.find(opt => opt.value === filters.status)?.label || filters.status}</strong>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Réinitialiser les filtres
            </button>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('bookings.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border transition flex items-center gap-2 ${
                showFilters || hasActiveFilters
                  ? 'bg-[#27489E] text-white border-[#27489E]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              {t('bookings.filters.title')}
              {hasActiveFilters && (
                <span className="bg-white text-[#27489E] rounded-full px-2 py-0.5 text-xs font-semibold">
                  {Object.values(filters).filter(v => v !== '').length}
                </span>
              )}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('bookings.filters.status')}</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('bookings.filters.paymentStatus')}</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                >
                  {paymentStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('bookings.filters.startDate')}</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('bookings.filters.endDate')}</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                />
              </div>

              {hasActiveFilters && (
                <div className="md:col-span-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t('bookings.filters.reset')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#27489E]"></div>
              <p className="mt-4 text-gray-600">{t('bookings.loading')}</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('bookings.noBookings')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedRows.length === bookings.length && bookings.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRows(bookings.map(b => b._id));
                            } else {
                              setSelectedRows([]);
                            }
                          }}
                          className="rounded border-gray-300 text-[#27489E] focus:ring-[#27489E]"
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('createdAt')}
                      >
                        {t('bookings.table.date')}
                        {sortConfig.key === 'createdAt' && (
                          <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.vehicle')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.client')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.vendor')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('dates.start')}
                      >
                        {t('bookings.table.period')}
                        {sortConfig.key === 'dates.start' && (
                          <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('financials.total')}
                      >
                        {t('bookings.table.amount')}
                        {sortConfig.key === 'financials.total' && (
                          <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('financials.vendorNet')}
                      >
                        MONTANT NET
                        {sortConfig.key === 'financials.vendorNet' && (
                          <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.payment')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MÉTHODE PAIEMENT
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('bookings.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookings.map((booking) => (
                      <tr key={booking._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(booking._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRows([...selectedRows, booking._id]);
                              } else {
                                setSelectedRows(selectedRows.filter(id => id !== booking._id));
                              }
                            }}
                            className="rounded border-gray-300 text-[#27489E] focus:ring-[#27489E]"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(booking.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {booking.vehicle?.image && (
                              <img
                                src={booking.vehicle.image}
                                alt={booking.vehicle.title}
                                className="w-12 h-12 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/48x48?text=No+Image';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">{booking.vehicle?.title || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {booking.client?.fullName || booking.client?.firstName || booking.client?.email || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">{booking.client?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {booking.vendor?.vendorDetails?.companyName || booking.vendor?.fullName || booking.vendor?.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div>{formatDate(booking.dates?.start)}</div>
                          <div className="text-xs text-gray-500">→ {formatDate(booking.dates?.end)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {formatCurrency(booking.financials?.total || 0, booking.financials?.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                          {formatCurrency(booking.financials?.vendorNet || 0, booking.financials?.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(booking.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPaymentStatusBadge(booking.financials?.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPaymentMethodBadge(booking.payment_method)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewDetails(booking._id)}
                              className="text-[#27489E] hover:text-[#1e3a7a] transition"
                              title={t('bookings.actions.viewDetails')}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(booking._id)}
                              className="text-blue-600 hover:text-blue-800 transition"
                              title={t('bookings.actions.edit')}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(booking._id)}
                              className="text-red-600 hover:text-red-800 transition"
                              title={t('bookings.actions.delete')}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700">
                    {t('bookings.pagination.showing', {
                      start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                      end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                      total: pagination.totalItems
                    })}
                  </span>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E]"
                  >
                    <option value={10}>{t('bookings.pagination.perPage', { count: 10 })}</option>
                    <option value={20}>{t('bookings.pagination.perPage', { count: 20 })}</option>
                    <option value={50}>{t('bookings.pagination.perPage', { count: 50 })}</option>
                    <option value={100}>{t('bookings.pagination.perPage', { count: 100 })}</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('bookings.pagination.previous')}
                  </button>
                  <span className="text-sm text-gray-700">
                    {t('bookings.pagination.pageInfo', {
                      current: pagination.currentPage,
                      total: pagination.totalPages
                    })}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                    disabled={currentPage === pagination.totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('bookings.pagination.next')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {detailDrawerOpen && (
        <>
          <div
            role="presentation"
            className="fixed inset-0 bg-black/40 z-[100]"
            onClick={closeDetailDrawer}
          />
          <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl z-[101] flex flex-col border-l border-gray-200">
            {selectedBookingId && (
              <BookingAdminDetail
                bookingId={selectedBookingId}
                onClose={closeDetailDrawer}
                onActionComplete={loadBookings}
              />
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default BookingsList;

